import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import rhService, { ApercuPaie, Bulletin, DeclarationCnss, Employe } from '../../api/rh';
import { formatTND } from '../../utils/formatters';

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const now = new Date();

const Paie: React.FC = () => {
  const [employes, setEmployes] = useState<Employe[]>([]);
  const [bulletins, setBulletins] = useState<Bulletin[]>([]);
  const [apercu, setApercu] = useState<ApercuPaie | null>(null);
  const [detail, setDetail] = useState<Bulletin | null>(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    employeId: '',
    mois: now.getMonth() + 1,
    annee: now.getFullYear(),
    primes: '',
  });

  const refresh = () =>
    rhService
      .listBulletins()
      .then(setBulletins)
      .catch((e: any) => {
        if (e?.response?.status !== 403) toast.error('Chargement impossible.');
      });

  useEffect(() => {
    rhService.listEmployes(true).then(setEmployes).catch(() => {});
    refresh();
  }, []);

  const employe = employes.find((e) => String(e.id) === form.employeId);

  // Aperçu temps réel du calcul dès qu'un salarié est choisi.
  useEffect(() => {
    if (!employe) {
      setApercu(null);
      return;
    }
    const brut = Number(employe.salaireBaseBrut || 0) + (Number(form.primes) || 0);
    rhService
      .apercuPaie(brut, employe.chefDeFamille, employe.nbEnfants)
      .then(setApercu)
      .catch(() => setApercu(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.employeId, form.primes]);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const generer = async () => {
    if (!form.employeId) {
      toast.error('Sélectionnez un salarié.');
      return;
    }
    setBusy(true);
    try {
      await rhService.genererBulletin(Number(form.employeId), form.mois, form.annee, Number(form.primes) || 0);
      toast.success('Bulletin généré et comptabilisé.');
      setForm({ ...form, primes: '' });
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Échec de la génération.');
    } finally {
      setBusy(false);
    }
  };

  const openPdf = async (id: number) => {
    try {
      const blob = await rhService.bulletinPdf(id);
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      toast.error('PDF indisponible.');
    }
  };

  // ---- Déclaration CNSS trimestrielle ----
  const [decForm, setDecForm] = useState({ annee: now.getFullYear(), trimestre: Math.floor(now.getMonth() / 3) + 1 });
  const [declaration, setDeclaration] = useState<DeclarationCnss | null>(null);
  const chargerDeclaration = async () => {
    try {
      setDeclaration(await rhService.declarationCnss(decForm.annee, decForm.trimestre));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Déclaration indisponible.');
    }
  };

  const supprimer = async (b: Bulletin) => {
    if (!window.confirm(`Supprimer le bulletin ${MOIS[b.mois - 1]} ${b.annee} de ${b.employeNom} ?`)) return;
    try {
      await rhService.deleteBulletin(b.id);
      toast.success('Bulletin supprimé.');
      if (detail?.id === b.id) setDetail(null);
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Suppression impossible.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Paie</h1>
        <p className="text-sm text-gray-500">
          Bulletins mensuels — CNSS (9,18 % / 16,57 %), IRPP au barème, CSS 1 %.
          Chaque bulletin génère l'écriture de salaires. <Link to="/rh" className="text-blue-600 hover:underline">← Module RH</Link>
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
        <h2 className="text-lg font-semibold">Générer un bulletin</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Salarié</label>
            <select value={form.employeId} onChange={(e) => set('employeId', e.target.value)} className={inputCls}>
              <option value="">— Choisir —</option>
              {employes.map((e) => (
                <option key={e.id} value={e.id}>{e.nomComplet} ({e.matricule})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Mois</label>
            <select value={form.mois} onChange={(e) => set('mois', Number(e.target.value))} className={inputCls}>
              {MOIS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Année</label>
            <input type="number" value={form.annee} onChange={(e) => set('annee', Number(e.target.value))} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Primes imposables</label>
            <input type="number" step="0.001" value={form.primes} onChange={(e) => set('primes', e.target.value)} className={inputCls} />
          </div>
        </div>

        {apercu && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 rounded-lg p-4 text-sm">
            <Stat label="Salaire brut" value={apercu.salaireBrut} />
            <Stat label="CNSS salariale" value={apercu.cnssSalarial} />
            <Stat label="IRPP" value={apercu.irpp} />
            <Stat label="CSS" value={apercu.css} />
            <Stat label="Net à payer" value={apercu.netAPayer} strong />
            <Stat label="CNSS patronale" value={apercu.cnssPatronal} />
            <Stat label="Coût employeur" value={apercu.salaireBrut + apercu.cnssPatronal} strong />
          </div>
        )}

        <button onClick={generer} disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
          {busy ? 'Génération…' : 'Générer le bulletin'}
        </button>
      </div>

      {detail && (
        <div className="bg-white p-6 rounded-xl border border-blue-200 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Bulletin {MOIS[detail.mois - 1]} {detail.annee} — {detail.employeNom}
            </h2>
            <button onClick={() => setDetail(null)} className="text-gray-400 hover:underline text-sm">Fermer</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <Stat label="Salaire de base" value={detail.salaireBase} />
            <Stat label="Primes" value={detail.primes} />
            <Stat label="Salaire brut" value={detail.salaireBrut} />
            <Stat label="CNSS salariale (9,18 %)" value={detail.cnssSalarial} />
            <Stat label="Frais professionnels" value={detail.fraisProfessionnels} />
            <Stat label="Base imposable (an)" value={detail.baseImposable} />
            <Stat label="IRPP" value={detail.irpp} />
            <Stat label="CSS" value={detail.css} />
            <Stat label="Net à payer" value={detail.netAPayer} strong />
            <Stat label="CNSS patronale (16,57 %)" value={detail.cnssPatronal} />
            <Stat label="Coût employeur" value={detail.coutEmployeur} strong />
          </div>
          {detail.ecritureNumero && (
            <p className="text-xs text-gray-500">
              Écriture comptable :{' '}
              <Link to="/comptabilite/journal" className="text-blue-600 hover:underline">{detail.ecritureNumero}</Link>
            </p>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3">Période</th>
              <th className="px-4 py-3">Salarié</th>
              <th className="px-4 py-3 text-right">Brut</th>
              <th className="px-4 py-3 text-right">Net à payer</th>
              <th className="px-4 py-3 text-right">Coût employeur</th>
              <th className="px-4 py-3">Écriture</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {bulletins.map((b) => (
              <tr key={b.id} className="border-t border-gray-100">
                <td className="px-4 py-3">{MOIS[b.mois - 1]} {b.annee}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{b.employeNom}</td>
                <td className="px-4 py-3 text-right">{formatTND(b.salaireBrut)}</td>
                <td className="px-4 py-3 text-right font-semibold">{formatTND(b.netAPayer)}</td>
                <td className="px-4 py-3 text-right">{formatTND(b.coutEmployeur)}</td>
                <td className="px-4 py-3 font-mono text-xs">{b.ecritureNumero || '—'}</td>
                <td className="px-4 py-3 text-right space-x-3">
                  <button onClick={() => setDetail(b)} className="text-blue-600 hover:underline">Détail</button>
                  <button onClick={() => openPdf(b.id)} className="text-blue-600 hover:underline">PDF</button>
                  <button onClick={() => supprimer(b)} className="text-red-600 hover:underline">Supprimer</button>
                </td>
              </tr>
            ))}
            {bulletins.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">Aucun bulletin.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
        <h2 className="text-lg font-semibold">Déclaration sociale CNSS (trimestrielle)</h2>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Année</label>
            <input type="number" value={decForm.annee}
                   onChange={(e) => setDecForm((f) => ({ ...f, annee: Number(e.target.value) }))}
                   className={inputCls + ' w-32'} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Trimestre</label>
            <select value={decForm.trimestre}
                    onChange={(e) => setDecForm((f) => ({ ...f, trimestre: Number(e.target.value) }))}
                    className={inputCls + ' w-32'}>
              {[1, 2, 3, 4].map((t) => <option key={t} value={t}>T{t}</option>)}
            </select>
          </div>
          <button onClick={chargerDeclaration}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
            Générer la déclaration
          </button>
        </div>

        {declaration && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-left">
                <tr>
                  <th className="px-4 py-3">Salarié</th>
                  <th className="px-4 py-3">N° CNSS</th>
                  <th className="px-4 py-3 text-right">Bulletins</th>
                  <th className="px-4 py-3 text-right">Brut</th>
                  <th className="px-4 py-3 text-right">CNSS salariale</th>
                  <th className="px-4 py-3 text-right">CNSS patronale</th>
                  <th className="px-4 py-3 text-right">Total CNSS</th>
                </tr>
              </thead>
              <tbody>
                {declaration.lignes.map((l) => (
                  <tr key={l.employeId} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-900">{l.employeNom}</td>
                    <td className="px-4 py-3 font-mono text-xs">{l.numeroCnss || '—'}</td>
                    <td className="px-4 py-3 text-right">{l.nbBulletins}</td>
                    <td className="px-4 py-3 text-right">{formatTND(l.brut)}</td>
                    <td className="px-4 py-3 text-right">{formatTND(l.cnssSalarial)}</td>
                    <td className="px-4 py-3 text-right">{formatTND(l.cnssPatronal)}</td>
                    <td className="px-4 py-3 text-right font-semibold">{formatTND(l.cnssTotal)}</td>
                  </tr>
                ))}
                {declaration.lignes.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Aucun bulletin sur ce trimestre.</td></tr>
                )}
              </tbody>
              {declaration.lignes.length > 0 && (
                <tfoot className="bg-gray-50 font-semibold">
                  <tr className="border-t-2 border-gray-200">
                    <td className="px-4 py-3" colSpan={3}>Total déclaration T{declaration.trimestre} {declaration.annee}</td>
                    <td className="px-4 py-3 text-right">{formatTND(declaration.totalBrut)}</td>
                    <td className="px-4 py-3 text-right">{formatTND(declaration.totalCnssSalarial)}</td>
                    <td className="px-4 py-3 text-right">{formatTND(declaration.totalCnssPatronal)}</td>
                    <td className="px-4 py-3 text-right">{formatTND(declaration.totalCnss)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2';

const Stat: React.FC<{ label: string; value: number; strong?: boolean }> = ({ label, value, strong }) => (
  <div>
    <div className="text-gray-500">{label}</div>
    <div className={strong ? 'font-bold text-gray-900' : 'text-gray-800'}>{formatTND(value)}</div>
  </div>
);

export default Paie;
